import React, { useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import {
  AlignUrduWrapper,
  Spacing,
  Typography,
  ImageContainer,
  FormattedBlanks,
  MarkdownWrapper,
  StyledDiv,
} from '@taleemabad/dsm/components';
import {
  formatBlanks,
  IAnswerOption,
  IQuestion,
  IQuestionMedia,
  QuestionFormats,
  QuestionSubType,
} from '@taleemabad/db';
import { isUrduText, toRoman } from '@taleemabad/dsm/utils';
import { MathJaxComponent } from '../math-jax/math-jax';
export interface MatchColumnsProps {
  questions: IQuestion[];
  showAnswer: boolean;
}

const Container = styled.div`
  margin-bottom: var(--rk-spacing-md);

  @media print {
    width: 100%;
    display: block;
  }
`;

const Column = styled.div`
  display: table-cell;
  width: 50%;
  padding: var(--rk-spacing-md);
  text-align: -webkit-center;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media print {
    display: table-cell !important;
    width: 50% !important;
    padding: var(--rk-spacing-sm) !important;
    vertical-align: top !important;
  }
`;

const Row = styled.div<{ showAnswer?: boolean }>`
  display: table;
  table-layout: fixed;
  width: 100%;
  margin-bottom: var(--rk-spacing-md);
  border-collapse: collapse;
  border-spacing: 0;
  background-color: ${({ showAnswer }) =>
    showAnswer ? 'var(--md-ref-secondary-70)' : 'transparent'};
  border-radius: 10px;

  @media print {
    page-break-inside: avoid;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
    background-color: transparent !important;
    border-radius: 0 !important;
    margin-bottom: var(--rk-spacing-sm) !important;
    border-spacing: 0 !important;
    border-collapse: collapse !important;
  }
`;

const GroupWrapper = styled.div`
  display: block;
  width: 100%;
`;

const HeaderRowWrapper = styled.div`
  margin: var(--rk-spacing-xl) 0;
`;

const shuffleOptions = (options: IAnswerOption[]) => {
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  return options;
};

const groupQuestions = (questions: IQuestion[]) => {
  return questions.reduce(
    (groups, question) => {
      if (question?.group) {
        const groupId = question.group.id;
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        groups[groupId].push(question);
      }
      return groups;
    },
    {} as { [key: string]: IQuestion[] },
  );
};

const renderQuestion = (
  statement: string,
  subType: string,
): React.ReactNode => {
  const hasFractions = /\[[^\]\n]{1,200}\]/.test(statement);
  if (
    subType === QuestionSubType.VerticalText ||
    subType === QuestionSubType.VerticalTextWithUnits ||
    subType === QuestionSubType.VerticalTextWithPlaceholder ||
    hasFractions
  ) {
    return (
      <MathJaxComponent
        question={statement}
        questionType={subType as QuestionSubType}
      />
    );
  } else {
    return (
      <FormattedBlanks>
        <MarkdownWrapper
          text={formatBlanks(statement)}
          variant="textBodyMediumSurface"
        />
      </FormattedBlanks>
    );
  }
};

interface ColumnComponentProps {
  format: string;
  statement: string;
  media: IQuestionMedia[];
  subType: string;
}

const renderColumnContent = (
  format: string,
  statement: string,
  media: IQuestionMedia[],
  subType: string,
): React.ReactNode => {
  if (format === QuestionFormats.StatementImage) {
    return (
      <>
        <Typography variant="textBodyMediumSurface">
          {renderQuestion(statement, subType)}
        </Typography>{' '}
        {media.map((mediaItem) => (
          <ImageContainer
            width='30%'
            key={mediaItem.url}
            src={mediaItem.url}
            alt="question image"
          />
        ))}
      </>
    );
  }
  if (format === QuestionFormats.Image) {
    return media.map((mediaItem) => (
      <ImageContainer
        width='30%'
        key={mediaItem.url}
        src={mediaItem.url}
        alt="question image"
      />
    ));
  }
  return (
    <Typography variant="textBodyMediumOnSurfaceVariant">
      {renderQuestion(statement, subType)}
    </Typography>
  );
};

const ColumnComponent: React.FC<ColumnComponentProps> = ({
  format,
  statement,
  media,
  subType,
}) => {
  return (
    <Column>
      {renderColumnContent(format, statement, media, subType)}
    </Column>
  );
};
interface GroupQuestionsProps {
  groupId: string;
  groupQuestions: IQuestion[];
  showAnswer: boolean;
  getShuffledAnswers: (groupQuestions: IQuestion[]) => IAnswerOption[];
}

const GroupQuestions: React.FC<GroupQuestionsProps> = ({
  groupId,
  groupQuestions,
  showAnswer,
  getShuffledAnswers,
}) => {
  const isUrdu = isUrduText(
    groupQuestions[0].questionStatement ?? groupQuestions[0]?.group?.titleText,
  );

  const shuffledAnswers = useMemo(
    () => getShuffledAnswers(groupQuestions),
    [groupQuestions, getShuffledAnswers],
  );

  return (
    <GroupWrapper>
      <AlignUrduWrapper key={groupId} isUrdu={isUrdu}>
        <HeaderRowWrapper>
          <Row>
            <Column>
              <Typography variant="textTitleMediumSurfaceVariant">
                {isUrdu ? 'کالم (الف)' : 'Column A'}
              </Typography>
            </Column>
            <Column>
              <Typography variant="textTitleMediumSurfaceVariant">
                {isUrdu ? 'کالم (ب)' : 'Column B'}
              </Typography>
            </Column>
          </Row>
        </HeaderRowWrapper>
        {groupQuestions.map((question, index) => (
          <Row key={question.id} showAnswer={showAnswer}>
            <ColumnComponent
              format={question.questionFormat}
              statement={question.questionStatement}
              media={question.questionMedia}
              subType={question.subType}
            />
            <ColumnComponent
              format={shuffledAnswers[index].answerFormat}
              statement={shuffledAnswers[index].statement}
              media={shuffledAnswers[index].media}
              subType={question.subType}
            />
          </Row>
        ))}
      </AlignUrduWrapper>
    </GroupWrapper>
  );
};
export const MatchColumns: React.FC<MatchColumnsProps> = ({
  questions,
  showAnswer,
}) => {
  const getShuffledAnswers = useCallback(
    (groupQuestions: IQuestion[]) => {
      const answers = groupQuestions.map((q) => q.answerOptions[0]);
      return showAnswer ? answers : shuffleOptions(answers);
    },
    [showAnswer],
  );
  const groupedQuestions = useMemo(
    () => groupQuestions(questions),
    [questions],
  );

  const isUrdu = isUrduText(
    questions[0].questionStatement ?? questions[0]?.group?.titleText,
  );

  return (
    <Container>
      {Object.entries(groupedQuestions).map(
        ([groupId, groupQuestions], index) => (
          <React.Fragment key={groupId}>
            <AlignUrduWrapper isUrdu={isUrdu}>
              {groupQuestions && (
                <StyledDiv marginTop={12}>
                  <Typography variant="textLabelLargeOnSurface">
                    {`${toRoman(index + 1)}. `}
                  </Typography>
                  <Typography variant="textBodyMediumSurface">
                    {groupQuestions[0].group?.titleText}
                  </Typography>
                </StyledDiv>
              )}
            </AlignUrduWrapper>
            <GroupQuestions
              groupId={groupId}
              groupQuestions={groupQuestions}
              showAnswer={showAnswer}
              getShuffledAnswers={getShuffledAnswers}
            />
          </React.Fragment>
        ),
      )}
    </Container>
  );
};
